import classNames from "classnames";
import { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import SearchBar from "./SearchBar";
import styles from "./TableHeading.module.css";

const DEBOUNCE_MS = 300;

interface Props {
  isSearchable?: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  searchDisabled?: boolean;
  searchButtons?: ReactNode | ReactNode[];
  className?: string;
}

const TableHeading = ({
  isSearchable,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  searchDisabled,
  searchButtons,
  className,
}: Props) => {
  const [inputValue, setInputValue] = useState(searchValue);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setInputValue(searchValue);
  }, [searchValue]);

  useEffect(
    () => () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    },
    [],
  );

  const handleChange = (nextValue: string) => {
    setInputValue(nextValue);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      onSearchChange(nextValue);
    }, DEBOUNCE_MS);
  };

  if (!isSearchable && !searchButtons) {
    return null;
  }

  return (
    <div className={classNames(styles.heading, className)}>
      <div className={styles.searchWrapper}>
        {isSearchable && (
          <SearchBar
            className={styles.search}
            placeholder={searchPlaceholder}
            value={inputValue}
            onChange={(term) => !searchDisabled && handleChange(term)}
          />
        )}
      </div>
      {(Array.isArray(searchButtons) ? searchButtons : [searchButtons]).map((button, index) => (
        <Fragment key={`button_${index}`}>{button}</Fragment>
      ))}
    </div>
  );
};

export default TableHeading;
